import fixtureJson from './fixtures/CHERYL_REVIEW_FRONTEND_FIXTURE.json'
import type {
    KnowledgeReviewBatchDetail,
    KnowledgeReviewBatchProjection,
    KnowledgeReviewCommand,
} from './knowledge-review-types'
import type { PostInterviewKnowledgeReviewPackage } from './meaning-compiler-types'

type FixturePayload = {
    fixtureOnly: true
    batch: KnowledgeReviewBatchProjection
    review: PostInterviewKnowledgeReviewPackage
}

const fixture = fixtureJson as unknown as FixturePayload
const storageKey = 'heft.knowledge-review.fixture.cheryl.v1'

function fixtureWorkingUnderstanding(): NonNullable<PostInterviewKnowledgeReviewPackage['workingUnderstanding']> {
    const sourceMeaningRecordRefs = Array.from(
        new Set(fixture.review.items.flatMap(item => item.sourceMeaningRecordRefs))
    )
    const sourceBoundedSnapshot = {
        snapshotId: 'fixture:cheryl-working-understanding-snapshot',
        snapshotRevision: fixture.review.sourceArtifact.sourceFingerprint,
        lifecycle: 'partial',
        profileSemanticRevision: fixture.review.sourceArtifact.semanticRevision,
        semanticRevision: fixture.review.sourceArtifact.semanticRevision,
        sourceMeaningRecordRefs: sourceMeaningRecordRefs.map(sourceMeaningRecordId => ({
            sourceMeaningRecordId,
            revision: 'fixture-v1',
            fingerprint: `fixture:${sourceMeaningRecordId}`,
        })),
        familyRevisionRefs: [],
    }
    const foundationReadBack = {
        destination: 'Subject knowledge · Cheryl Habbe',
        destinationState: 'BOUND' as const,
        currentDirection:
            'Cheryl appears to be moving toward deeper, more strategic marketing and brand work with sustained ownership of the problem and its outcomes.',
        currentDesiredState:
            'Find a role, preferably outside an advertising agency, where she can understand the customer and business more deeply and stay close enough to learn what happens after the work ships.',
        proposedPaths: ['Brand management', 'Strategic marketing leadership'],
        target: 'A role with deeper business and customer proximity.',
        strengths: [
            'Connecting business problems with specialist work across marketing, brand, web, and creative contexts.',
            'Building shared understanding in unfamiliar situations and helping teams move forward.',
            'Bringing useful, clear, non-manipulative intent to marketing work.',
        ],
        workPreferences: [
            'She wants sustained ownership and learning rather than one-off campaign handoffs.',
            'She prefers useful, clear work over manipulation or attention for its own sake.',
        ],
        supportedTfd: [],
        gaps: [
            'Which role types best provide the depth and ownership Cheryl wants next?',
            'How much direct people-management responsibility does she want?',
            'Which outcomes from her strongest work can she substantiate with evidence?',
        ],
        sections: [],
        proposalRefs: [],
        sourceMeaningRecordRefs,
        evidenceRefs: [],
    }
    const editorialThesis = {
        statement:
            'Cheryl appears strongest as a cross-functional marketing integrator: someone who gets underneath the problem, understands how specialist work fits together, and helps people move when the answer is unclear.',
        definingIdeas: [
            'Her experience spans brand, strategic marketing, web, usability, and campaign work.',
            'She values useful, clear, non-manipulative communication.',
            'She is seeking more depth, business proximity, and longitudinal learning.',
        ],
        importantDistinctions: [
            'She does not claim to personally perform every specialist function.',
            'Her preferred professional label remains unsettled.',
        ],
        trajectory:
            'From broad agency and digital experience toward deeper strategic ownership and brand-side proximity.',
        authorizedDirection:
            'The direction is a working read-back for Cheryl to confirm, not a settled identity claim.',
    }
    return {
        understandingId: 'fixture:cheryl-working-understanding',
        revision: 'fixture:cheryl-working-understanding-v1',
        displayRevision: 'working-understanding-display-v3',
        snapshotFingerprint: fixture.review.sourceArtifact.sourceFingerprint,
        narrative:
            'Cheryl describes a broad marketing and digital career shaped by curiosity, adaptability, and a desire to understand the problem beneath the deliverable. She is now looking for more depth, business proximity, and time to learn from outcomes.',
        currentDirection: foundationReadBack.currentDirection,
        consequentialUnknowns: foundationReadBack.gaps,
        sourceMeaningRecordRefs,
        relationshipRefs: [],
        foundationProposalRefs: [],
        expressedSections: [],
        inferredSections: [],
        foundationReadBack,
        sourceBoundedSnapshot,
        currentRead: {
            currentReadId: 'fixture:cheryl-current-read',
            revision: 'fixture:cheryl-current-read-v1',
            fingerprint: 'fixture:cheryl-current-read-fingerprint',
            profileKnowledgeSnapshotRef: { ref: 'fixture:cheryl-profile-knowledge-snapshot' },
            editorialThesis,
            sourceMeaningRecordRefs,
            canonicalRefs: [],
            interpretationRefs: [],
            qualificationRefs: [],
            explicitUnknowns: foundationReadBack.gaps,
            authorityClass: 'derived_editorial_interpretation',
            canonicalTruth: false,
            lifecycle: 'review_required',
        },
        authorityClass: 'derived_editorial_interpretation',
        canonicalTruth: false,
        lifecycle: 'review_required',
        createdAt: '2026-09-08T17:17:36.562Z',
    }
}

export function isCherylKnowledgeReviewFixture(search = window.location.search): boolean {
    return import.meta.env.DEV && new URLSearchParams(search).get('reviewFixture') === 'cheryl'
}

export function isCherylFixtureBatchId(batchId: string | undefined): boolean {
    return import.meta.env.DEV && batchId === fixture.batch.batchId
}

export function preserveCherylFixtureNavigation(path: string, batchId?: string): string {
    const fixtureActive = isCherylKnowledgeReviewFixture() || isCherylFixtureBatchId(batchId)
    if (!fixtureActive) return path
    return `${path}${path.includes('?') ? '&' : '?'}reviewFixture=cheryl`
}

export function getCherylFixtureBatchId(): string {
    return fixture.batch.batchId
}

export function isCherylFixtureReviewItems(reviewItemIds: string[]): boolean {
    if (!import.meta.env.DEV) return false
    const fixtureItemIds = new Set(fixture.review.items.map(item => item.reviewItemId))
    return reviewItemIds.length > 0 && reviewItemIds.every(reviewItemId => fixtureItemIds.has(reviewItemId))
}

function readStoredReview(): PostInterviewKnowledgeReviewPackage {
    try {
        const stored = window.sessionStorage.getItem(storageKey)
        if (stored) return JSON.parse(stored) as PostInterviewKnowledgeReviewPackage
    } catch {
        // Fixture persistence is best-effort and must never affect the real review route.
    }
    const review = structuredClone(fixture.review)
    return review.workingUnderstanding ? review : { ...review, workingUnderstanding: fixtureWorkingUnderstanding() }
}

function writeStoredReview(review: PostInterviewKnowledgeReviewPackage): void {
    try {
        window.sessionStorage.setItem(storageKey, JSON.stringify(review))
    } catch {
        // Fixture persistence is best-effort only.
    }
}

function reviewSummary(review: PostInterviewKnowledgeReviewPackage) {
    const items = review.items
    const pending = items.filter(item => item.reviewStatus === 'pending').length
    const accepted = items.filter(item => item.reviewStatus === 'accepted').length
    const corrected = items.filter(item => item.reviewStatus === 'corrected').length
    const rejected = items.filter(item => item.reviewStatus === 'rejected').length
    const clarificationRequired = items.filter(item => item.reviewStatus === 'clarification_required').length
    const readyToAccept = items.filter(
        item => item.reviewStatus === 'pending' && item.attentionReasons.length === 0 && item.standing === 'candidate'
    ).length
    const needsALook = items.filter(item => item.reviewStatus === 'pending' && item.attentionReasons.length > 0).length
    return {
        ...review.summary,
        totalItems: items.length,
        pending,
        accepted,
        corrected,
        rejected,
        clarificationRequired,
        readyToAccept,
        needsALook,
        needsClarification: clarificationRequired,
        familyCounts: items.reduce<Record<string, number>>((counts, item) => {
            counts[item.family] = (counts[item.family] ?? 0) + 1
            return counts
        }, {}),
    }
}

function projectBatch(review: PostInterviewKnowledgeReviewPackage): KnowledgeReviewBatchProjection {
    const summary = reviewSummary(review)
    const reviewed = summary.accepted + summary.corrected + summary.rejected
    const understandingConfirmed = review.workingUnderstanding?.lifecycle === 'confirmed'
    const complete = understandingConfirmed || summary.pending === 0
    return {
        ...fixture.batch,
        status: complete ? 'review_complete' : review.status === 'in_review' ? 'in_review' : 'ready_for_review',
        sourceStatus: complete ? 'completed' : review.status,
        updatedAt: new Date().toISOString(),
        progress: {
            reviewed,
            total: summary.totalItems,
            percent: summary.totalItems === 0 ? 0 : Math.round((reviewed / summary.totalItems) * 100),
        },
        proposedCount: summary.totalItems,
        clarificationCount: summary.clarificationRequired,
        primaryAction: complete ? 'view_outcome' : 'open_review',
        admission: {
            ...fixture.batch.admission,
            eligible: complete && (understandingConfirmed || summary.clarificationRequired === 0),
            candidateCount: understandingConfirmed
                ? review.items.filter(item => item.standing === 'candidate').length
                : summary.accepted + summary.corrected,
        },
    }
}

export function getCherylFixtureQueue(): {
    batches: KnowledgeReviewBatchProjection[]
    pendingCount: number
    totalCount: number
} {
    const review = readStoredReview()
    const batch = projectBatch(review)
    return { batches: [batch], pendingCount: batch.status === 'review_complete' ? 0 : 1, totalCount: 1 }
}

export function getCherylFixtureBatchDetail(batchId: string): KnowledgeReviewBatchDetail | null {
    if (batchId !== fixture.batch.batchId) return null
    const review = readStoredReview()
    return { batch: projectBatch(review), review }
}

function updateItemStatus(
    review: PostInterviewKnowledgeReviewPackage,
    reviewItemId: string,
    status: 'accepted' | 'corrected' | 'rejected' | 'clarification_required',
    correctedRepresentation?: string
): PostInterviewKnowledgeReviewPackage {
    return {
        ...review,
        status: 'in_review',
        packageRevision: review.packageRevision + 1,
        items: review.items.map(item =>
            item.reviewItemId === reviewItemId
                ? {
                      ...item,
                      reviewStatus: status,
                      ...(correctedRepresentation ? { proposedRepresentation: correctedRepresentation } : {}),
                  }
                : item
        ),
    }
}

export function dispatchCherylFixtureCommand(command: KnowledgeReviewCommand): {
    package: PostInterviewKnowledgeReviewPackage
    idempotentReplay: boolean
} {
    const current = readStoredReview()
    let next = current
    if (command.type === 'start_review_batch' || command.type === 'resume_review_batch') {
        next = { ...current, status: 'in_review', packageRevision: current.packageRevision + 1 }
    } else if (command.type === 'confirm_working_understanding' && current.workingUnderstanding) {
        next = {
            ...current,
            packageRevision: current.packageRevision + 1,
            workingUnderstanding: { ...current.workingUnderstanding, lifecycle: 'confirmed' },
        }
    } else if (command.type === 'correct_working_understanding' && current.workingUnderstanding) {
        const corrected = command.correctedRepresentation?.trim() ?? ''
        next = {
            ...current,
            packageRevision: current.packageRevision + 1,
            workingUnderstanding: {
                ...current.workingUnderstanding,
                narrative: corrected,
                revision: `working-understanding-fixture:${Date.now()}`,
                lifecycle: 'review_required',
            },
            sourceAddenda: [
                ...(current.sourceAddenda ?? []),
                {
                    sourceArtifactRef: `${current.sourceArtifact.artifactId}:v${current.sourceArtifact.artifactVersion}`,
                    sourceKind: 'human_review_addendum',
                    reviewItemId: 'working-understanding',
                    content: corrected,
                    contentFingerprint: `fixture:${Date.now()}`,
                    createdBy: command.actorId ?? 'fixture-reviewer',
                    createdAt: command.issuedAt ?? new Date().toISOString(),
                    status: 'pending_semantic_reprocessing',
                },
            ],
        }
    } else if (command.type === 'accept_ready_review_items') {
        next = {
            ...current,
            status: 'in_review',
            packageRevision: current.packageRevision + 1,
            items: current.items.map(item =>
                item.reviewStatus === 'pending' && item.attentionReasons.length === 0
                    ? { ...item, reviewStatus: 'accepted' }
                    : item
            ),
        }
    } else if (command.reviewItemId) {
        const statusByCommand = {
            accept_review_item: 'accepted',
            correct_review_item: 'corrected',
            reject_review_item: 'rejected',
            clarify_review_item: 'clarification_required',
        } as const
        const status = statusByCommand[command.type as keyof typeof statusByCommand]
        if (status) next = updateItemStatus(current, command.reviewItemId, status, command.correctedRepresentation)
    }
    if (
        command.type === 'finish_review_batch' &&
        (next.workingUnderstanding?.lifecycle === 'confirmed' ||
            next.items.every(item => item.reviewStatus !== 'pending'))
    ) {
        next = { ...next, status: 'completed', packageRevision: next.packageRevision + 1 }
    }
    next = { ...next, summary: reviewSummary(next) }
    writeStoredReview(next)
    return { package: next, idempotentReplay: false }
}

export function clearCherylKnowledgeReviewFixture(): void {
    window.sessionStorage.removeItem(storageKey)
}
